import {
  BodyText,
  breakpointsRem,
  Card,
  cssBase,
  Grid,
  Headline,
  rem,
  SpaceBetween,
  spacing,
  spacingScaled,
  styled,
} from '@lessonup/ui-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'usehooks-ts';
import { useFeatureFlags } from '../../utils/featureFlags/useFeatureFlags';
import { AiToolboxGenerator, generators } from './AiToolbox.types';
import { i18nextNamespaceAiTools } from './AiToolbox.utils';
import { Disclaimer } from './Disclaimer';
import { FeedbackButton } from './FeedbackButton';
import { MissingFlagHelper } from './helpers/MissingFlagHelper';

export const AiToolboxOverview = ({ navigateToGenerator }: { navigateToGenerator: (slug: string) => void }) => {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const isMobile = useMediaQuery(`(max-width: ${breakpointsRem.breakPoint500})`);
  const isLargeDesktop = useMediaQuery(`(min-width: ${breakpointsRem.breakPoint900})`);
  const { isFeatureEnabled } = useFeatureFlags();

  const availableGenerators = generators.filter(
    (generator) => !generator.featureFlag || isFeatureEnabled(generator.featureFlag)
  );

  const handleClickGenerator = (generator: AiToolboxGenerator) => {
    if (generator.action === 'default') {
      navigateToGenerator(generator.slug);
    } else {
      generator.onClick();
    }
  };

  return (
    <MissingFlagHelper>
      <StyledWrapper>
        <Header direction="y" spacing={spacing.size8}>
          <Headline size="large">{t('aiToolsOverviewTitle')}</Headline>
          <StyledDescription size="medium">{t('aiToolsOverviewDescription')}</StyledDescription>
          <div>
            <FeedbackButton />
          </div>
        </Header>
        <StyledGrid gap={spacing.size16} cols={isMobile ? 1 : isLargeDesktop ? 4 : 2} alignItems="flex-start">
          {availableGenerators.map((generator) => (
            <React.Fragment key={generator.type}>
              <StyledCard onClick={() => handleClickGenerator(generator)} cardTheme="ai" showBorder={false}>
                <StyleCardGrid gap={spacing.size8} cols={2} justifyItems="start" alignItems="start">
                  <generator.icon width={IconSize} height={IconSize} className={iconStyle} />
                  <StyledHeadline size="small">{t(generator.labelKey)}</StyledHeadline>
                  <div></div>
                  <BodyText size="small">{t(generator.descriptionKey)}</BodyText>
                </StyleCardGrid>
              </StyledCard>
            </React.Fragment>
          ))}
        </StyledGrid>
        <Disclaimer />
      </StyledWrapper>
    </MissingFlagHelper>
  );
};

const StyledHeadline = styled(Headline)`
  font-size: 1.3rem;
`;

const Header = styled(SpaceBetween)`
  text-align: center;
`;

const IconSize = '1.7rem';

// because the text always looks aligned to the bottom
const iconStyle = cssBase`
  margin-top: 0.1rem;
`;

const StyleCardGrid = styled(Grid)`
  grid-template-columns: ${IconSize} 1fr;
`;

const StyledCard = styled(Card)`
  width: 100%;
  height: 100%;
  padding: ${spacingScaled(spacing.size16, 'small')};
  display: inline-flex;
  align-items: start;
`;

const StyledWrapper = styled.div`
  max-width: ${rem('1344px')};
  margin: 0 auto;
  padding: ${spacing.size32};
`;

const StyledGrid = styled(Grid)`
  margin-top: ${spacing.size32};
`;

const StyledDescription = styled(BodyText)`
  max-width: ${rem('640px')};
  margin: 0 auto;
`;
